import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useTranslate } from '@tolgee/react';
import {
  Button,
  Dropdown,
  Form,
  InputGroup
} from 'react-bootstrap';
import {
  ArrowLeft,
  Download,
  Eye,
  EyeSlash,
  LayerBackward,
  LayerForward,
  Lock,
  TextCenter,
  TextLeft,
  TextRight,
  Trash3,
  TypeBold,
  TypeItalic,
  Unlock
} from 'react-bootstrap-icons';
import SimpleBar from 'simplebar-react';
import 'simplebar-react/dist/simplebar.min.css';
import toast from 'react-hot-toast';
import { PinturaEditor } from '@pqina/react-pintura';
import {
  createDefaultImageReader,
  createDefaultImageWriter,
  getEditorDefaults,
  locale_en_gb,
  plugin_annotate,
  setPlugins,
  type ImageSource,
  type PinturaEditorOptions,
  type Shape
} from '@pqina/pintura';
import {
  LocaleAnnotate,
  LocaleCore,
  LocaleMarkupEditor
} from '@pqina/pintura/locale/en_GB';
import {
  LocaleAnnotate as LocaleAnnotateDe,
  LocaleCore as LocaleCoreDe,
  LocaleMarkupEditor as LocaleMarkupEditorDe
} from '@pqina/pintura/locale/de_DE';
import '@pqina/pintura/pintura.css';
import './LomavisTemplateEditor.css';
import { LomavisConfirmationModal } from '../../../molecules/feedback/LomavisConfirmationModal';
import {
  LomavisFontTypeahead,
  type LomavisFontOption
} from '../../../atoms/inputs/LomavisFontTypeahead';
import { LomavisSketchColorPicker } from '../../../atoms/inputs/LomavisSketchColorPicker';
import { LomavisDropdown1 } from '../../../atoms/overlays/LomavisDropdown1';
import { LomavisOverlayToolTip1 } from '../../../atoms/overlays/lomavisOverlayToolTip1/lomavisOverlayToolTip1';
import { LomavisImageUpload1 } from '../../../molecules/inputs/LomavisImageUpload1';
import {
  canvasPresets,
  createLayer,
  hasBackgroundLayer,
  resizeTemplateCanvas,
  serializeTemplate,
  TEMPLATE_EDITOR_THUMBNAIL_WIDTH,
  toPinturaShape,
  pinturaShapeToLayerPatch,
  type TemplateDefinition,
  type TemplateLayer,
  type TemplateLayerKind
} from './templateDefinition';

setPlugins(plugin_annotate);

/** Language used for the editor interface. Use `en` for English or `de` for German. */
export type TemplateEditorLanguage = 'en' | 'de';

/** A font offered in the text-layer font picker. */
export type TemplateEditorFontOption = LomavisFontOption;

/** Functions supplied to `onExportActionsReady` for exports initiated outside the editor. */
export type TemplateEditorExportActions = {
  /** Sends the serialised template JSON to `onExportTemplate`. */
  exportTemplate: () => void;
  /** Creates a full-size PNG and sends it to `onExportImage`. */
  exportPng: () => Promise<File | undefined>;
  /** Creates a full-size JPEG and sends it to `onExportImage`. */
  exportJpg: () => Promise<File | undefined>;
  /** Creates a PNG thumbnail and sends it to `onExportImage`. */
  exportThumbnail: () => Promise<File | undefined>;
};

/** File type produced by the built-in Save button. Defaults to `json`. */
export type TemplateEditorSaveFormat = 'json' | 'png' | 'jpg' | 'thumbnail';

/** Data emitted after the built-in Save button completes. */
export type TemplateEditorSavePayload =
  | { format: 'json'; template: TemplateDefinition; json: string }
  | { format: 'png' | 'jpg' | 'thumbnail'; file: File; width?: number };

const defaultFontOptions: TemplateEditorFontOption[] = [
  { value: 'Arial', label: 'Arial' },
  { value: 'Georgia', label: 'Georgia' },
  { value: 'Helvetica', label: 'Helvetica' },
  { value: 'Verdana', label: 'Verdana' }
];

const BUNNY_FONTS_CSS_URL = 'https://fonts.bunny.net/css';
const loadedBunnyFontFamilies = new Set<string>();

const loadBunnyFontOptions = async (): Promise<TemplateEditorFontOption[]> => {
  const { default: catalogue } = await import(
    '../../../../pages/settings/branding/template_branding/webfonts.json'
  );
  return catalogue.items.map((font) => ({
    value: font.family,
    label: font.family
  }));
};

const loadBunnyFont = (
  font: TemplateEditorFontOption,
  onLoad?: () => void
): void => {
  if (
    loadedBunnyFontFamilies.has(font.value) ||
    typeof document === 'undefined'
  )
    return;

  loadedBunnyFontFamilies.add(font.value);
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = `${BUNNY_FONTS_CSS_URL}?family=${encodeURIComponent(
    font.value
  ).replace(/%20/g, '+')}:400,400i,700,700i&display=swap`;
  link.addEventListener('load', onLoad ?? (() => undefined));
  link.addEventListener('error', () =>
    loadedBunnyFontFamilies.delete(font.value)
  );
  document.head.append(link);
};

const getPinturaEditorDefaults = (language: TemplateEditorLanguage) =>
  getEditorDefaults({
    utils: ['annotate'],
    imageReader: createDefaultImageReader(),
    imageWriter: createDefaultImageWriter(),
    locale:
      language === 'de'
        ? { ...LocaleCoreDe, ...LocaleAnnotateDe, ...LocaleMarkupEditorDe }
        : { ...LocaleCore, ...LocaleAnnotate, ...LocaleMarkupEditor }
  });

export type LomavisTemplateEditorProps = {
  /** Current, application-owned template data. */
  value: TemplateDefinition;
  /** Receives every edit; update the `value` passed back to the editor. */
  onChange: (template: TemplateDefinition) => void;
  /** Receives the template and its serialised JSON when a JSON export occurs. */
  onExportTemplate?: (template: TemplateDefinition, json: string) => void;
  /** Receives an exported PNG, JPG, or thumbnail file without downloading it. */
  onExportImage?: (
    file: File,
    options: { format: 'png' | 'jpg' | 'thumbnail'; width?: number }
  ) => void;
  /** Receives export functions for buttons or actions outside the editor. */
  onExportActionsReady?: (actions: TemplateEditorExportActions) => void;
  /** Chooses the file type made by Save: JSON, PNG, JPG, or a PNG thumbnail. */
  saveFormat?: TemplateEditorSaveFormat;
  /** Receives the completed Save result in the format selected by `saveFormat`. */
  onSave?: (payload: TemplateEditorSavePayload) => void;
  /** Disables the Save action while the host is persisting the template. */
  isSaving?: boolean;
  /** Shows a back icon in the header and runs this callback when it is clicked. */
  onBack?: () => void;
  /** Accessible label for the back action. */
  backLabel?: string;
  /** Shows a header icon that immediately downloads the current canvas as a PNG. */
  showDownloadButton?: boolean;
  /** Optional upload handler. Resolve with the public image URL to use after upload. */
  onUploadImage?: (file: File) => Promise<string>;
  /** Interface language: `en` for English or `de` for German. Defaults to English. */
  language?: TemplateEditorLanguage;
  /** Extra fonts in the text picker, shown before the built-in fallback fonts. */
  fontOptions?: TemplateEditorFontOption[];
  /** Additional CSS class names for the editor's outer container. */
  className?: string;
};

/**
 * Draws the artboard as a white sheet against the grey workspace. Pintura
 * renders this inside its canvas, so it tracks the image exactly — a plain DOM
 * element cannot, as the image is positioned inside the WebGL canvas.
 */
const ARTBOARD_BACKGROUND = [1, 1, 1, 1];

const backgroundSource = (width: number, height: number, color: string) =>
  `data:image/svg+xml,${encodeURIComponent(
    color === 'transparent'
      ? `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}"/>`
      : `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}"><rect width="100%" height="100%" fill="${color}"/></svg>`
  )}`;

const getImageDimensions = async (
  file: File
): Promise<{ width: number; height: number }> =>
  await new Promise((resolve, reject) => {
    const source = URL.createObjectURL(file);
    const image = new Image();
    const cleanup = () => URL.revokeObjectURL(source);

    image.onload = () => {
      cleanup();
      resolve({ width: image.naturalWidth, height: image.naturalHeight });
    };
    image.onerror = () => {
      cleanup();
      reject(new Error('Unable to determine image dimensions'));
    };
    image.src = source;
  });

const getImageLayerBounds = (
  image: { width: number; height: number },
  canvas: TemplateDefinition['canvas'],
  maximumCanvasRatio = 0.8
) => {
  const scale = Math.min(
    (canvas.width * maximumCanvasRatio) / image.width,
    (canvas.height * maximumCanvasRatio) / image.height
  );
  const size = { width: image.width * scale, height: image.height * scale };

  return {
    size,
    position: {
      x: (canvas.width - size.width) / 2,
      y: (canvas.height - size.height) / 2
    }
  };
};

const getCanvasDimension = (value: string): number => {
  const dimension = Number(value);
  return Number.isFinite(dimension) && dimension > 0
    ? Math.max(1, Math.round(dimension))
    : 1;
};

type DropdownOption = {
  value: string;
  label: React.ReactNode;
};

const BootstrapDropdown: React.FC<{
  value: string;
  options: DropdownOption[];
  onChange: (value: string) => void;
  disabled?: boolean;
  ariaLabel: string;
}> = ({ value, options, onChange, disabled = false, ariaLabel }) => {
  return (
    <LomavisDropdown1
      options={options}
      value={value}
      onChange={onChange}
      disabled={disabled}
      ariaLabel={ariaLabel}
    />
  );
};

interface AddLayerToggleProps extends React.ComponentProps<typeof Button> {
  className?: string;
}

const AddLayerToggle = React.forwardRef<HTMLButtonElement, AddLayerToggleProps>(
  ({ className, children, ...props }, ref) => (
    <Button
      {...props}
      ref={ref}
      size="sm"
      variant="outline-primary"
      className={`d-inline-flex align-items-center justify-content-center rounded-3 p-0 fw-bold ${
        className?.replace('dropdown-toggle', '') ?? ''
      }`}
      style={{ width: 32, height: 32 }}
    >
      {children}
    </Button>
  )
);

AddLayerToggle.displayName = 'AddLayerToggle';

export const LomavisTemplateEditor: React.FC<LomavisTemplateEditorProps> = ({
  value,
  onChange,
  onBack,
  backLabel,
  onExportTemplate,
  onExportImage,
  onExportActionsReady,
  saveFormat = 'json',
  onSave,
  isSaving = false,
  showDownloadButton = false,
  onUploadImage,
  language = 'en',
  fontOptions = [],
  className
}) => {
  const { t: translate } = useTranslate();
  const t = useCallback(
    (key: string, defaultValue: string) =>
      translate({ key, defaultValue, language }),
    [language, translate]
  );
  const resolvedBackLabel =
    backLabel ?? t('templateEditor.actions.back', 'Back');
  const editorRef = useRef<PinturaEditor>(null);
  const workspaceRef = useRef<HTMLDivElement>(null);
  const valueRef = useRef(value);
  const previousTemplateIdRef = useRef(value.id);
  const onExportTemplateRef = useRef(onExportTemplate);
  const onExportImageRef = useRef(onExportImage);
  const onSaveRef = useRef(onSave);
  valueRef.current = value;
  onExportTemplateRef.current = onExportTemplate;
  onExportImageRef.current = onExportImage;
  onSaveRef.current = onSave;
  const [tab, setTab] = useState('layers');
  const [selectedLayerId, setSelectedLayerId] = useState<string | null>(
    value.layers[0]?.id ?? null
  );
  const [selectionRevision, setSelectionRevision] = useState(0);
  const [fontLoadRevision, setFontLoadRevision] = useState(0);
  const [bunnyFontOptions, setBunnyFontOptions] = useState<
    TemplateEditorFontOption[]
  >([]);
  const [hasLoadedBunnyFontOptions, setHasLoadedBunnyFontOptions] =
    useState(false);
  const selectedLayer = value.layers.find(
    (layer) => layer.id === selectedLayerId
  );
  useEffect(() => {
    const templateChanged = previousTemplateIdRef.current !== value.id;
    previousTemplateIdRef.current = value.id;
    setSelectedLayerId((currentId) => {
      if (
        !templateChanged &&
        currentId &&
        value.layers.some((layer) => layer.id === currentId)
      ) {
        return currentId;
      }
      return value.layers[0]?.id ?? null;
    });
    if (templateChanged || value.layers.length === 0) setTab('layers');
  }, [value.id, value.layers]);
  const shapes = useMemo(
    () =>
      value.layers.map((layer) => {
        const shape = toPinturaShape(layer);
        return {
          ...shape,
          isSelected:
            layer.id === selectedLayerId && !shape.disableSelect
        };
      }),
    [fontLoadRevision, selectedLayerId, selectionRevision, value.layers]
  );
  const pinturaEditorDefaults = useMemo(
    () => getPinturaEditorDefaults(language),
    [language]
  );
  const availableFontOptions = useMemo(() => {
    const byValue = new Map<string, TemplateEditorFontOption>();
    [...fontOptions, ...bunnyFontOptions, ...defaultFontOptions].forEach(
      (font) => {
        if (!byValue.has(font.value)) byValue.set(font.value, font);
      }
    );
    return [...byValue.values()];
  }, [bunnyFontOptions, fontOptions]);
  const ensureBunnyFont = useCallback((font: TemplateEditorFontOption) => {
    loadBunnyFont(font, () => setFontLoadRevision((revision) => revision + 1));
  }, []);
  const loadFontCatalogue = useCallback(() => {
    if (hasLoadedBunnyFontOptions) return;
    setHasLoadedBunnyFontOptions(true);
    void loadBunnyFontOptions()
      .then(setBunnyFontOptions)
      .catch(() => setHasLoadedBunnyFontOptions(false));
  }, [hasLoadedBunnyFontOptions]);
  useEffect(() => {
    const fontsByFamily = new Map(
      availableFontOptions.map((font) => [font.value, font])
    );
    value.layers.forEach((layer) => {
      if (layer.kind === 'text') {
        ensureBunnyFont(
          fontsByFamily.get(layer.typography.fontFamily) ?? {
            value: layer.typography.fontFamily,
            label: layer.typography.fontFamily
          }
        );
      }
    });
  }, [availableFontOptions, ensureBunnyFont, value.layers]);
  const update = (
    updater: (template: TemplateDefinition) => TemplateDefinition
  ) => onChange(updater(valueRef.current));
  const updateLayer = (
    id: string,
    updater: (layer: TemplateLayer) => TemplateLayer
  ) =>
    update((template) => ({
      ...template,
      layers: template.layers.map((layer) =>
        layer.id === id ? updater(layer) : layer
      )
    }));
  const selectLayer = (id: string, openEdit = false) => {
    setSelectedLayerId(id);
    // Re-apply selection even when this row was already active but Pintura's
    // canvas selection was cleared independently.
    setSelectionRevision((revision) => revision + 1);
    if (openEdit) setTab('edit');
  };
  const backgroundExists = hasBackgroundLayer(value.layers);
  const addLayer = (
    kind: TemplateLayerKind,
    shapeType?: TemplateLayer['shapeType']
  ) => {
    if (kind === 'background' && backgroundExists) {
      toast(
        t(
          'templateEditor.background.alreadyExists',
          'A template can only have one background layer.'
        )
      );
      return;
    }
    const layer = createLayer(kind, value.canvas, shapeType);
    update((template) => ({
      ...template,
      // The background enters at the bottom of the stack and stays there.
      layers:
        kind === 'background'
          ? [layer, ...template.layers]
          : [...template.layers, layer]
    }));
    selectLayer(layer.id, true);
  };
  const updateShape = (shape: Shape) => {
    const id = (shape as Shape & { id?: string }).id;
    if (!id) return;
    const target = value.layers.find((layer) => layer.id === id);
    if (!target) return;
    updateLayer(id, (layer) => ({
      ...layer,
      ...pinturaShapeToLayerPatch(shape, value.canvas, layer)
    }));
  };
  const moveLayer = (direction: -1 | 1) => {
    if (!selectedLayerId) return;
    update((template) => {
      const index = template.layers.findIndex(
        (layer) => layer.id === selectedLayerId
      );
      const target = index + direction;
      if (index < 0 || target < 0 || target >= template.layers.length)
        return template;
      if (template.layers[index].locked || template.layers[target].locked)
        return template;
      // Nothing swaps with the background: it neither rises nor gets buried.
      if (
        template.layers[index].kind === 'background' ||
        template.layers[target].kind === 'background'
      )
        return template;
      const layers = [...template.layers];
      [layers[index], layers[target]] = [layers[target], layers[index]];
      return { ...template, layers };
    });
  };
  const exportTemplate = useCallback(() => {
    const template = valueRef.current;
    onExportTemplateRef.current?.(template, serializeTemplate(template));
  }, []);
  const exportImage = useCallback(
    async (
      mimeType: 'image/png' | 'image/jpeg',
      format: 'png' | 'jpg' | 'thumbnail',
      width?: number
    ) => {
      try {
        const processingOptions: PinturaEditorOptions = {
          imageReader: createDefaultImageReader(),
          imageWriter: createDefaultImageWriter({
            mimeType,
            quality: 0.92,
            targetSize: width ? { width, upscale: false } : undefined
          }),
          // JPEG cannot preserve transparency, so its empty pixels use the same
          // white background that users see on the editor artboard.
          imageBackgroundColor:
            mimeType === 'image/jpeg' ? ARTBOARD_BACKGROUND : undefined,
          locale: locale_en_gb
        };
        // Pintura applies writer overrides from the first argument when it is
        // processing the currently loaded image. Passing these as the second
        // argument is only honored while loading a new source.
        const result = await editorRef.current?.editor.processImage(
          processingOptions as unknown as ImageSource
        );
        if (result?.dest) {
          onExportImageRef.current?.(result.dest, { format, width });
          return result.dest;
        }
      } catch (error) {
        window.alert(
          error instanceof Error
            ? error.message
            : t(
                'templateEditor.errors.export',
                'Unable to export this overlay.'
              )
        );
      }
      return undefined;
    },
    [t]
  );
  const exportActions = useMemo<TemplateEditorExportActions>(
    () => ({
      exportTemplate,
      exportPng: async () => await exportImage('image/png', 'png'),
      exportJpg: async () => await exportImage('image/jpeg', 'jpg'),
      exportThumbnail: async () =>
        await exportImage(
          'image/png',
          'thumbnail',
          TEMPLATE_EDITOR_THUMBNAIL_WIDTH
        )
    }),
    [exportImage, exportTemplate]
  );
  useEffect(() => {
    onExportActionsReady?.(exportActions);
  }, [exportActions, onExportActionsReady]);
  const save = useCallback(async () => {
    try {
      if (saveFormat === 'json') {
        const template = valueRef.current;
        const json = serializeTemplate(template);
        onExportTemplateRef.current?.(template, json);
        onSaveRef.current?.({ format: 'json', template, json });
        return;
      }
      const imageOptions: {
        mimeType: 'image/png' | 'image/jpeg';
        format: 'png' | 'jpg' | 'thumbnail';
        width?: number;
      } =
        saveFormat === 'jpg'
          ? { mimeType: 'image/jpeg' as const, format: 'jpg' as const }
          : saveFormat === 'thumbnail'
          ? {
              mimeType: 'image/png' as const,
              format: 'thumbnail' as const,
              width: TEMPLATE_EDITOR_THUMBNAIL_WIDTH
            }
          : { mimeType: 'image/png' as const, format: 'png' as const };
      const file = await exportImage(
        imageOptions.mimeType,
        imageOptions.format,
        imageOptions.width
      );
      if (file) {
        onSaveRef.current?.({
          format: imageOptions.format,
          file,
          width: imageOptions.width
        });
      }
    } catch (error) {
      window.alert(
        error instanceof Error
          ? error.message
          : t('templateEditor.errors.save', 'Unable to save this overlay.')
      );
    }
  }, [exportImage, saveFormat, t]);
  const downloadPng = useCallback(async () => {
    const file = await exportImage('image/png', 'png');
    if (!file) return;

    const link = document.createElement('a');
    const filename = valueRef.current.name?.trim() || 'Overlay 1';
    link.href = URL.createObjectURL(file);
    link.download = `${filename.replace(/[\\/:*?"<>|]/g, '-')}.png`;
    document.body.append(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(link.href);
  }, [exportImage]);
  const replaceImage = async (file?: File): Promise<void> => {
    if (!file || !selectedLayer) return;
    const imageDimensions = await getImageDimensions(file).catch(
      () => undefined
    );
    const setImageSource = (src: string) =>
      updateLayer(selectedLayer.id, (layer) => ({
        ...layer,
        ...((layer.kind === 'image' || layer.kind === 'background') &&
        imageDimensions
          ? getImageLayerBounds(
              imageDimensions,
              valueRef.current.canvas,
              layer.kind === 'background' ? 1 : 0.8
            )
          : {}),
        image: { ...layer.image, fit: 'contain', src }
      }));

    if (onUploadImage) {
      try {
        const src = await onUploadImage(file);
        setImageSource(src);
      } catch {
        toast.error(
          t(
            'templateEditor.errors.imageUpload',
            'Unable to upload the image. You can keep editing and try again.'
          )
        );
      }
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setImageSource(String(reader.result));
    reader.onerror = () =>
      toast.error(
        t(
          'templateEditor.errors.imageRead',
          'Unable to read the image. You can keep editing and try again.'
        )
      );
    reader.readAsDataURL(file);
  };
  const setCanvas = (presetId: string) => {
    if (presetId === 'custom')
      return update((template) => ({
        ...template,
        canvas: { ...template.canvas, preset: 'custom' }
      }));
    const preset = canvasPresets.find((item) => item.id === presetId);
    if (preset)
      update((template) =>
        resizeTemplateCanvas(template, preset.width, preset.height, preset.id)
      );
  };
  const addLayerOptions: Array<{
    kind: TemplateLayerKind;
    title: string;
    subtitle: string;
  }> = [
    {
      kind: 'text',
      title: t('templateEditor.actions.addTextLayer', 'Text layer'),
      subtitle: t(
        'templateEditor.actions.addTextLayerDescription',
        'Add editable text to your design.'
      )
    },
    {
      kind: 'shape',
      title: t('templateEditor.actions.addShapeLayer', 'Shape layer'),
      subtitle: t(
        'templateEditor.actions.addShapeLayerDescription',
        'Add a rectangle, circle, or line.'
      )
    },
    {
      kind: 'image',
      title: t('templateEditor.actions.addImageLayer', 'Image layer'),
      subtitle: t(
        'templateEditor.actions.addImageLayerDescription',
        'Upload and position an image.'
      )
    },
    {
      kind: 'background',
      title: t('templateEditor.actions.addBackgroundLayer', 'Background layer'),
      subtitle: t(
        'templateEditor.actions.addBackgroundLayerDescription',
        'Set the canvas color or background image.'
      )
    }
  ];
  const renderAddLayerOptions = () =>
    addLayerOptions.map(({ kind, title, subtitle }) => (
      <Dropdown.Item key={kind} onClick={() => addLayer(kind)}>
        <span className="lomavis-template-add-layer-title">{title}</span>
        <span className="lomavis-template-add-layer-subtitle">{subtitle}</span>
      </Dropdown.Item>
    ));

  return (
    <div
      className={`d-flex h-100 w-100 flex-column overflow-hidden border rounded-3 bg-white ${
        className ?? ''
      }`}
      style={{
        height: '100%',
        color: 'var(--bs-gray-900, #071437)',
        fontFamily: 'Inter, Helvetica, sans-serif'
      }}
    >
      <header className="d-flex flex-wrap align-items-center gap-2 border-bottom p-3">
        {onBack && (
          <LomavisOverlayToolTip1
            placement="bottom"
            creatorStyle
            text={resolvedBackLabel}
          >
            <Button
              type="button"
              size="sm"
              variant="light"
              className="lomavis-template-layer-action bg-white text-gray-700"
              aria-label={resolvedBackLabel}
              onClick={onBack}
            >
              <ArrowLeft aria-hidden="true" />
            </Button>
          </LomavisOverlayToolTip1>
        )}
        <Form.Group style={{ width: 345, maxWidth: '100%' }}>
          <Form.Control
            aria-label={t('templateEditor.templateName', 'Overlay name')}
            className="fw-semibold"
            value={value.name ?? ''}
            placeholder={t('templateEditor.defaultName', 'Overlay 1')}
            onChange={(event) =>
              update((template) => ({
                ...template,
                name: event.target.value
              }))
            }
            onBlur={() => {
              if (valueRef.current.name.trim()) return;
              update((template) => ({
                ...template,
                name: t('templateEditor.defaultName', 'Overlay 1')
              }));
            }}
          />
        </Form.Group>
        <Form.Group style={{ width: 250, maxWidth: '100%' }}>
          <BootstrapDropdown
            value={value.canvas.preset}
            ariaLabel={t('templateEditor.canvasSize', 'Canvas size')}
            onChange={setCanvas}
            options={[
              ...canvasPresets.map((preset) => ({
                value: preset.id,
                label: `${t(
                  `templateEditor.presets.${preset.id}`,
                  preset.label
                )} · ${preset.width} × ${preset.height}`
              })),
              {
                value: 'custom',
                label: t('templateEditor.presets.custom', 'Custom…')
              }
            ]}
          />
          {value.canvas.preset === 'custom' && (
            <InputGroup className="mt-2">
              <Form.Control
                type="number"
                min="1"
                value={value.canvas.width}
                aria-label={t('templateEditor.canvasWidth', 'Canvas width')}
                onChange={(event) =>
                  update((template) =>
                    resizeTemplateCanvas(
                      template,
                      getCanvasDimension(event.target.value),
                      template.canvas.height,
                      'custom'
                    )
                  )
                }
              />
              <InputGroup.Text>×</InputGroup.Text>
              <Form.Control
                type="number"
                min="1"
                value={value.canvas.height}
                aria-label={t('templateEditor.canvasHeight', 'Canvas height')}
                onChange={(event) =>
                  update((template) =>
                    resizeTemplateCanvas(
                      template,
                      template.canvas.width,
                      getCanvasDimension(event.target.value),
                      'custom'
                    )
                  )
                }
              />
            </InputGroup>
          )}
        </Form.Group>
        <div className="ms-auto d-flex align-items-center gap-2">
          {showDownloadButton && (
            <LomavisOverlayToolTip1
              placement="bottom"
              creatorStyle
              text={t(
                'templateEditor.actions.downloadPng',
                'Download PNG'
              )}
            >
              <Button
                type="button"
                size="sm"
                variant="light"
                className="lomavis-template-layer-action bg-white text-gray-700"
                aria-label={t(
                  'templateEditor.actions.downloadPng',
                  'Download PNG'
                )}
                onClick={() => {
                  void downloadPng();
                }}
              >
                <Download aria-hidden="true" />
              </Button>
            </LomavisOverlayToolTip1>
          )}
          <Button
            size="sm"
            variant="primary"
            className="rounded-3 px-4 fw-semibold shadow-sm"
            disabled={isSaving}
            onClick={() => {
              void save();
            }}
          >
            {isSaving
              ? t('templateEditor.actions.saving', 'Saving…')
              : t('templateEditor.actions.save', 'Save')}
          </Button>
        </div>
      </header>
      <main
        className="d-grid flex-grow-1 overflow-hidden"
        style={{
          gridTemplateColumns: 'minmax(0, 1fr) 360px',
          minHeight: 0,
          flexBasis: 0
        }}
      >
        <section
          className="lomavis-template-workspace d-flex flex-column overflow-hidden"
          style={{ minWidth: 0, minHeight: 0 }}
        >
          <div
            ref={workspaceRef}
            className="position-relative flex-grow-1"
            style={{ minHeight: 0, touchAction: 'none' }}
          >
            <div className="position-relative h-100">
              <PinturaEditor
                {...pinturaEditorDefaults}
                key={`${value.canvas.width}x${value.canvas.height}`}
                ref={editorRef}
                className="h-100"
                imageBackgroundColor={ARTBOARD_BACKGROUND}
                src={backgroundSource(
                  value.canvas.width,
                  value.canvas.height,
                  value.canvas.backgroundColor
                )}
                imageAnnotation={shapes}
                enableToolbar={false}
                enableUtils={false}
                enableZoom={false}
                enableZoomControls={false}
                enableZoomInput={false}
                enableTapToAddText={false}
                enableMoveTool
                enableAutoSelectMoveTool
                markupEditorToolbar={[]}
                markupEditorToolRetainStyles={false}
                onSelectshape={(shape) => {
                  const id = (shape as Shape & { id?: string }).id;
                  if (id) selectLayer(id);
                }}
                onUpdateshape={updateShape}
              />
            </div>
            {value.layers.length === 0 && (
              <div
                className="position-absolute top-50 start-50 translate-middle text-center text-dark"
                style={{ width: 280, zIndex: 2 }}
              >
                <div className="mb-2 fs-5 fw-bold">
                  {t('templateEditor.empty.title', 'Start your overlay')}
                </div>
                <p className="mb-3 text-muted">
                  {t(
                    'templateEditor.empty.description',
                    'Add a layer to start building your design.'
                  )}
                </p>
                <Dropdown>
                  <Dropdown.Toggle
                    size="sm"
                    variant="primary"
                    className="rounded-3 px-3 fw-semibold shadow-sm"
                    aria-label={t('templateEditor.actions.add', 'Add layer')}
                  >
                    {t('templateEditor.actions.add', 'Add layer')}
                  </Dropdown.Toggle>
                  <Dropdown.Menu className="lomavis-template-action-menu border-0 p-2 shadow">
                    {renderAddLayerOptions()}
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            )}
          </div>
        </section>
        <aside
          className="d-flex h-100 flex-column overflow-hidden border-start bg-white"
          style={{ minHeight: 0 }}
        >
          <div className="d-flex flex-shrink-0 align-items-center gap-2 border-bottom bg-white px-3 py-2">
            <div
              role="tablist"
              aria-label={t(
                'templateEditor.sidebarLabel',
                'Overlay editor sidebar'
              )}
              className="d-flex flex-grow-1 rounded-3 bg-light p-1"
            >
              <Button
                size="sm"
                variant="light"
                role="tab"
                aria-selected={tab === 'layers'}
                className={`flex-grow-1 rounded-3 border-0 py-1 fw-medium ${
                  tab === 'layers'
                    ? 'bg-white text-dark shadow-sm'
                    : 'bg-transparent text-muted shadow-none'
                }`}
                onClick={() => setTab('layers')}
              >
                {t('templateEditor.tabs.layers', 'Layers')}
              </Button>
              <Button
                size="sm"
                variant="light"
                role="tab"
                aria-selected={tab === 'edit'}
                disabled={!selectedLayer}
                className={`flex-grow-1 rounded-3 border-0 py-1 fw-medium ${
                  tab === 'edit'
                    ? 'bg-white text-dark shadow-sm'
                    : 'bg-transparent text-muted shadow-none'
                }`}
                onClick={() => setTab('edit')}
              >
                {t('templateEditor.tabs.editLayer', 'Edit layer')}
              </Button>
            </div>
            <Dropdown align="end">
              <Dropdown.Toggle
                as={AddLayerToggle}
                aria-label={t('templateEditor.actions.add', 'Add layer')}
              >
                +
              </Dropdown.Toggle>
              <Dropdown.Menu
                align="end"
                flip={false}
                className="lomavis-template-action-menu border-0 p-2 shadow"
              >
                {renderAddLayerOptions()}
              </Dropdown.Menu>
            </Dropdown>
          </div>
          <SimpleBar
            forceVisible="y"
            autoHide={false}
            className="lomavis-template-sidebar-scroll flex-grow-1"
            style={{ minHeight: 0, height: '100%', flexBasis: 0 }}
          >
            {tab === 'layers' ? (
              <div>
                <div className="lomavis-template-layer-list p-2">
                  {value.layers.map((layer) => (
                    <div
                      role="button"
                      tabIndex={0}
                      key={layer.id}
                      className={`lomavis-template-layer-list-item d-grid w-100 align-items-center gap-2 p-2 text-start ${
                        selectedLayerId === layer.id
                          ? 'is-selected'
                          : ''
                      }`}
                      style={{
                        gridTemplateColumns: '28px minmax(0, 1fr) 28px 28px'
                      }}
                      onClick={() => selectLayer(layer.id)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          selectLayer(layer.id);
                        }
                      }}
                      >
                      <span
                        className={`lomavis-template-layer-kind lomavis-template-layer-kind-${layer.kind}`}
                      >
                        {layer.kind === 'text'
                          ? 'T'
                          : layer.kind === 'image'
                          ? '▧'
                          : layer.kind === 'background'
                          ? '▣'
                          : '□'}
                      </span>
                      <span className="text-truncate fw-semibold">
                        {layer.name}
                      </span>
                      <Button
                        type="button"
                        variant="link"
                        className="lomavis-template-layer-list-action"
                        aria-label={t(
                          'templateEditor.actions.toggleVisibility',
                          'Toggle layer visibility'
                        )}
                        onClick={(event) => {
                          event.stopPropagation();
                          updateLayer(layer.id, (current) => ({
                            ...current,
                            visible: !current.visible
                          }));
                        }}
                      >
                        {layer.visible ? <Eye /> : <EyeSlash />}
                      </Button>
                      <Button
                        type="button"
                        variant="link"
                        className="lomavis-template-layer-list-action"
                        aria-label={t(
                          'templateEditor.actions.toggleLock',
                          'Toggle layer lock'
                        )}
                        onClick={(event) => {
                          event.stopPropagation();
                          updateLayer(layer.id, (current) => ({
                            ...current,
                            locked: !current.locked
                          }));
                        }}
                      >
                        {layer.locked ? <Lock /> : <Unlock />}
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            ) : selectedLayer ? (
              <LayerFields
                layer={selectedLayer}
                language={language}
                fontOptions={availableFontOptions}
                onFontSelect={ensureBunnyFont}
                onFontPickerFocus={loadFontCatalogue}
                onUpdate={(updater) => updateLayer(selectedLayer.id, updater)}
                onMove={moveLayer}
                onDelete={() => {
                  update((template) => ({
                    ...template,
                    layers: template.layers.filter(
                      (layer) => layer.id !== selectedLayer.id
                    )
                  }));
                  setSelectedLayerId(
                    value.layers.find((layer) => layer.id !== selectedLayer.id)
                      ?.id ?? null
                  );
                  setTab('layers');
                }}
                onImageSelect={async (file) => {
                  await replaceImage(file);
                }}
                onImageRemove={() =>
                  updateLayer(selectedLayer.id, (layer) => ({
                    ...layer,
                    image: { ...layer.image, fit: 'contain', src: undefined }
                  }))
                }
              />
            ) : null}
          </SimpleBar>
        </aside>
      </main>
    </div>
  );
};

const LayerFields: React.FC<{
  layer: TemplateLayer;
  language: TemplateEditorLanguage;
  fontOptions: TemplateEditorFontOption[];
  onFontSelect: (font: TemplateEditorFontOption) => void;
  onFontPickerFocus: () => void;
  onUpdate: (updater: (layer: TemplateLayer) => TemplateLayer) => void;
  onMove: (direction: -1 | 1) => void;
  onDelete: () => void;
  onImageSelect: (file: File) => void | Promise<void>;
  onImageRemove: () => void;
}> = ({
  layer,
  language,
  fontOptions,
  onFontSelect,
  onFontPickerFocus,
  onUpdate,
  onMove,
  onDelete,
  onImageSelect,
  onImageRemove
}) => {
  const { t: translate } = useTranslate();
  const t = useCallback(
    (key: string, defaultValue: string) =>
      translate({ key, defaultValue, language }),
    [language, translate]
  );
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const pintura: Record<string, unknown> =
    layer.kind === 'text'
      ? { text: layer.text, ...layer.typography }
      : layer.kind === 'shape'
      ? {
          backgroundColor: layer.fillColor,
          cornerRadius: layer.cornerRadius,
          shapeType: layer.shapeType ?? 'rectangle'
        }
      : layer.kind === 'background'
      ? {
          backgroundColor: layer.fillColor ?? '#ffffff',
          backgroundImage: layer.image?.src
        }
      : {
          backgroundImage: layer.image?.src,
          backgroundSize:
            layer.image?.fit === 'stretch'
              ? 'stretch'
              : layer.image?.fit === 'natural'
              ? 'natural'
              : layer.image?.fit ?? 'contain',
          cornerRadius: layer.cornerRadius,
          opacity: layer.image?.opacity ?? 1,
          borderColor: layer.image?.borderColor ?? '#000000',
          borderWidth: layer.image?.borderWidth ?? 0
        };
  const setPintura = (property: string, fieldValue: unknown) =>
    onUpdate((current) => {
      if (current.kind === 'text') {
        if (property === 'text') {
          const text = String(fieldValue);
          const typography = current.typography;
          const lineCount = text.split('\n').length;
          const minimumHeight = Math.ceil(
            lineCount * typography.fontSize * typography.lineHeight +
              typography.fontSize * 0.35
          );
          return {
            ...current,
            text,
            size: {
              ...current.size,
              height: Math.max(current.size.height, minimumHeight)
            }
          };
        }
        const typography = { ...current.typography };
        Object.assign(typography, { [property]: fieldValue });
        return { ...current, typography };
      }
      if (current.kind === 'background')
        return property === 'backgroundColor'
          ? { ...current, fillColor: String(fieldValue) }
          : current;
      if (current.kind === 'shape')
        return property === 'backgroundColor'
          ? { ...current, fillColor: String(fieldValue) }
          : property === 'shapeType'
          ? {
              ...current,
              shapeType: fieldValue as TemplateLayer['shapeType'],
              ...(fieldValue === 'straightLine'
                ? {
                    size: {
                      ...current.size,
                      height: Math.min(current.size.height, 12)
                    }
                  }
                : {})
            }
          : { ...current, cornerRadius: Number(fieldValue) };
      if (property === 'cornerRadius') {
        return { ...current, cornerRadius: Math.max(0, Number(fieldValue)) };
      }
      if (property === 'opacity') {
        return {
          ...current,
          image: {
            ...current.image,
            fit: current.image?.fit ?? 'contain',
            opacity: Math.min(1, Math.max(0, Number(fieldValue)))
          }
        };
      }
      if (property === 'borderColor' || property === 'borderWidth') {
        return {
          ...current,
          image: {
            ...current.image,
            fit: current.image?.fit ?? 'contain',
            [property]:
              property === 'borderWidth'
                ? Math.max(0, Number(fieldValue))
                : String(fieldValue)
          }
        };
      }
      const fit =
        fieldValue === 'contain'
          ? 'contain'
          : fieldValue === 'stretch'
          ? 'stretch'
          : fieldValue === 'natural'
          ? 'natural'
          : 'cover';
      return {
        ...current,
        image: { ...current.image, src: current.image?.src, fit }
      };
    });
  const backgroundFillIsTransparent = layer.fillColor === 'transparent';
  const selectedFontFamily = String(pintura.fontFamily ?? 'Arial');
  const typeaheadFontOptions = fontOptions.some(
    (font) => font.value === selectedFontFamily
  )
    ? fontOptions
    : [
        { value: selectedFontFamily, label: selectedFontFamily },
        ...fontOptions
      ];
  return (
    <div>
      <div className="d-flex align-items-center justify-content-between gap-2 border-bottom p-3">
        <div className="min-w-0">
          <strong>{t('templateEditor.tabs.editLayer', 'Edit layer')}</strong>
          <small className="d-block text-muted">{layer.name}</small>
        </div>
        <div className="d-flex flex-shrink-0 align-items-center gap-1">
          {layer.kind !== 'background' && (
            <>
              <LomavisOverlayToolTip1
                placement="bottom"
                creatorStyle
                text={t(
                  'templateEditor.actions.sendBackHelp',
                  'Move this layer one position backward.'
                )}
              >
                <Button
                  type="button"
                  size="sm"
                  variant="light"
                  className="lomavis-template-layer-action bg-white text-gray-700"
                  aria-label={t(
                    'templateEditor.actions.sendBack',
                    'Send back'
                  )}
                  disabled={layer.locked}
                  onClick={() => onMove(-1)}
                >
                  <LayerBackward />
                </Button>
              </LomavisOverlayToolTip1>
              <LomavisOverlayToolTip1
                placement="bottom"
                creatorStyle
                text={t(
                  'templateEditor.actions.bringForwardHelp',
                  'Move this layer one position forward.'
                )}
              >
                <Button
                  type="button"
                  size="sm"
                  variant="light"
                  className="lomavis-template-layer-action bg-white text-gray-700"
                  aria-label={t(
                    'templateEditor.actions.bringForward',
                    'Bring forward'
                  )}
                  disabled={layer.locked}
                  onClick={() => onMove(1)}
                >
                  <LayerForward />
                </Button>
              </LomavisOverlayToolTip1>
            </>
          )}
          <LomavisOverlayToolTip1
            placement="bottom"
            creatorStyle
            text={t(
              'templateEditor.actions.deleteLayerHelp',
              'Permanently remove this layer from the overlay.'
            )}
          >
            <Button
              type="button"
              size="sm"
              variant="light"
              className="lomavis-template-layer-action bg-white text-danger"
              aria-label={t(
                'templateEditor.actions.deleteLayer',
                'Delete layer'
              )}
              disabled={layer.locked}
              onClick={() => setShowDeleteConfirmation(true)}
            >
              <Trash3 />
            </Button>
          </LomavisOverlayToolTip1>
        </div>
      </div>
      <div className="lomavis-template-layer-fields d-grid gap-2 p-3">
        <Form.Group>
          <Form.Label>{t('templateEditor.layerName', 'Layer name')}</Form.Label>
          <div className="d-flex align-items-stretch gap-2">
            <Form.Control
              value={layer.name}
              disabled={layer.locked}
              onChange={(event) =>
                onUpdate((current) => ({
                  ...current,
                  name: event.target.value
                }))
              }
            />
            <div className="d-flex flex-shrink-0 align-items-stretch gap-1">
              <LomavisOverlayToolTip1
                placement="top"
                creatorStyle
                text={
                  layer.visible
                    ? t(
                        'templateEditor.actions.hideLayerHelp',
                        'Hide this layer from the overlay.'
                      )
                    : t(
                        'templateEditor.actions.showLayerHelp',
                        'Show this layer in the overlay.'
                      )
                }
              >
                <Button
                  type="button"
                  size="sm"
                  variant="light"
                  className="lomavis-template-layer-action lomavis-template-layer-name-action bg-white text-gray-700"
                  aria-label={t(
                    'templateEditor.actions.toggleVisibility',
                    'Toggle layer visibility'
                  )}
                  onClick={() =>
                    onUpdate((current) => ({
                      ...current,
                      visible: !current.visible
                    }))
                  }
                >
                  {layer.visible ? <Eye /> : <EyeSlash />}
                </Button>
              </LomavisOverlayToolTip1>
              <LomavisOverlayToolTip1
                placement="top"
                creatorStyle
                text={
                  layer.locked
                    ? t(
                        'templateEditor.actions.unlockLayerHelp',
                        'Unlock this layer to edit its properties.'
                      )
                    : t(
                        'templateEditor.actions.lockLayerHelp',
                        'Lock this layer to prevent edits.'
                      )
                }
              >
                <Button
                  type="button"
                  size="sm"
                  variant="light"
                  className="lomavis-template-layer-action lomavis-template-layer-name-action bg-white text-gray-700"
                  aria-label={t(
                    'templateEditor.actions.toggleLock',
                    'Toggle layer lock'
                  )}
                  onClick={() =>
                    onUpdate((current) => ({
                      ...current,
                      locked: !current.locked
                    }))
                  }
                >
                  {layer.locked ? <Lock /> : <Unlock />}
                </Button>
              </LomavisOverlayToolTip1>
            </div>
          </div>
        </Form.Group>
        {layer.kind === 'text' && (
          <>
            <div className="border-top pt-2 text-uppercase text-muted small fw-bold">
              {t('templateEditor.typography', 'Typography')}
            </div>
            <Form.Group>
              <Form.Label>
                {t('templateEditor.text.content', 'Text')}
              </Form.Label>
              <Form.Control
                as="textarea"
                value={String(pintura.text ?? '')}
                disabled={layer.locked}
                onChange={(event) => setPintura('text', event.target.value)}
              />
            </Form.Group>
            <div
              className="d-grid gap-2"
              style={{ gridTemplateColumns: '1fr 1fr' }}
            >
              <Form.Group>
                <Form.Label>{t('templateEditor.text.font', 'Font')}</Form.Label>
                <LomavisFontTypeahead
                  id={`template-editor-font-${layer.id}`}
                  options={typeaheadFontOptions}
                  value={selectedFontFamily}
                  disabled={layer.locked}
                  placeholder={t(
                    'templateEditor.text.searchFont',
                    'Search fonts…'
                  )}
                  onFocus={onFontPickerFocus}
                  onPreview={loadBunnyFont}
                  onSelect={(font) => {
                    onFontSelect(font);
                    setPintura('fontFamily', font.value);
                  }}
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>{t('templateEditor.text.size', 'Size')}</Form.Label>
                <Form.Control
                  type="number"
                  value={Number(pintura.fontSize ?? 48)}
                  disabled={layer.locked}
                  onChange={(event) =>
                    setPintura('fontSize', Number(event.target.value))
                  }
                />
              </Form.Group>
            </div>
            <Form.Group>
              <Form.Label>
                {t('templateEditor.text.formatting', 'Formatting')}
              </Form.Label>
              <div className="lomavis-template-text-toolbar" role="toolbar">
                <Button
                  type="button"
                  size="sm"
                  variant={
                    pintura.fontWeight === 'bold' ? 'primary' : 'light'
                  }
                  className="lomavis-template-text-toolbar-button"
                  aria-label={t('templateEditor.text.bold', 'Bold')}
                  aria-pressed={pintura.fontWeight === 'bold'}
                  disabled={layer.locked}
                  onClick={() =>
                    setPintura(
                      'fontWeight',
                      pintura.fontWeight === 'bold' ? 'normal' : 'bold'
                    )
                  }
                >
                  <TypeBold aria-hidden="true" />
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={
                    pintura.fontStyle === 'italic' ? 'primary' : 'light'
                  }
                  className="lomavis-template-text-toolbar-button"
                  aria-label={t('templateEditor.text.italic', 'Italic')}
                  aria-pressed={pintura.fontStyle === 'italic'}
                  disabled={layer.locked}
                  onClick={() =>
                    setPintura(
                      'fontStyle',
                      pintura.fontStyle === 'italic' ? 'normal' : 'italic'
                    )
                  }
                >
                  <TypeItalic aria-hidden="true" />
                </Button>
                <span className="lomavis-template-text-toolbar-divider" />
                {[
                  {
                    value: 'left',
                    label: t('templateEditor.text.left', 'Align left'),
                    icon: TextLeft
                  },
                  {
                    value: 'center',
                    label: t('templateEditor.text.center', 'Align center'),
                    icon: TextCenter
                  },
                  {
                    value: 'right',
                    label: t('templateEditor.text.right', 'Align right'),
                    icon: TextRight
                  }
                ].map(({ value, label, icon: Icon }) => (
                  <Button
                    key={value}
                    type="button"
                    size="sm"
                    variant={
                      (pintura.textAlign ?? 'left') === value
                        ? 'primary'
                        : 'light'
                    }
                    className="lomavis-template-text-toolbar-button"
                    aria-label={label}
                    aria-pressed={(pintura.textAlign ?? 'left') === value}
                    disabled={layer.locked}
                    onClick={() => setPintura('textAlign', value)}
                  >
                    <Icon aria-hidden="true" />
                  </Button>
                ))}
                <span className="lomavis-template-text-toolbar-divider" />
                <LomavisSketchColorPicker
                  compact
                  value={String(pintura.color ?? '#000000')}
                  disabled={layer.locked}
                  ariaLabel={t('templateEditor.text.color', 'Text color')}
                  onChange={(value) => setPintura('color', value)}
                />
              </div>
            </Form.Group>
            <div
              className="d-grid gap-2"
              style={{ gridTemplateColumns: '1fr 1fr' }}
            >
              <Form.Group>
                <Form.Label>
                  {t('templateEditor.text.lineHeight', 'Line height')}
                </Form.Label>
                <Form.Control
                  type="number"
                  min="0.5"
                  step="0.05"
                  value={Number(pintura.lineHeight ?? 1.1)}
                  disabled={layer.locked}
                  onChange={(event) =>
                    setPintura('lineHeight', Number(event.target.value))
                  }
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>
                  {t('templateEditor.text.letterSpacing', 'Letter spacing')}
                </Form.Label>
                <Form.Control
                  type="number"
                  step="0.1"
                  value={Number(pintura.letterSpacing ?? 0)}
                  disabled={layer.locked}
                  onChange={(event) =>
                    setPintura('letterSpacing', Number(event.target.value))
                  }
                />
              </Form.Group>
            </div>
          </>
        )}
        {layer.kind === 'shape' && (
          <>
            <Form.Group>
              <Form.Label>{t('templateEditor.shape.type', 'Shape')}</Form.Label>
              <BootstrapDropdown
                value={String(pintura.shapeType ?? 'rectangle')}
                disabled={layer.locked}
                ariaLabel={t('templateEditor.shape.type', 'Shape')}
                onChange={(value) => setPintura('shapeType', value)}
                options={[
                  {
                    value: 'rectangle',
                    label: t('templateEditor.shape.rectangle', 'Rectangle')
                  },
                  {
                    value: 'ellipse',
                    label: t('templateEditor.shape.ellipse', 'Ellipse')
                  },
                  {
                    value: 'triangle',
                    label: t('templateEditor.shape.triangle', 'Triangle')
                  },
                  {
                    value: 'straightLine',
                    label: t(
                      'templateEditor.shape.straightLine',
                      'Straight line'
                    )
                  },
                  {
                    value: 'wavyLine',
                    label: t('templateEditor.shape.wavyLine', 'Wavy line')
                  },
                  {
                    value: 'arrow',
                    label: t('templateEditor.shape.arrow', 'Arrow')
                  },
                  {
                    value: 'diamond',
                    label: t('templateEditor.shape.diamond', 'Diamond')
                  },
                  {
                    value: 'star',
                    label: t('templateEditor.shape.star', 'Star')
                  }
                ]}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>
                {t('templateEditor.shape.fill', 'Fill color')}
              </Form.Label>
              <LomavisSketchColorPicker
                value={String(pintura.backgroundColor ?? '#3359c7')}
                disabled={layer.locked}
                ariaLabel={t('templateEditor.shape.fill', 'Fill color')}
                onChange={(value) => setPintura('backgroundColor', value)}
              />
            </Form.Group>
            {((layer.shapeType ?? 'rectangle') === 'rectangle' ||
              layer.shapeType === 'straightLine') && (
              <Form.Group>
                <Form.Label>
                  {t('templateEditor.shape.cornerRadius', 'Corner radius')}
                </Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  value={Number(pintura.cornerRadius ?? 0)}
                  disabled={layer.locked}
                  onChange={(event) =>
                    setPintura('cornerRadius', Number(event.target.value))
                  }
                />
              </Form.Group>
            )}
          </>
        )}
        {layer.kind === 'background' && (
          <>
            <div className="text-muted small">
              {t(
                'templateEditor.background.hint',
                'The background stays behind every other layer. Select it on the canvas to move, resize, or rotate its image.'
              )}
            </div>
            <Form.Group>
              <Form.Label>
                {t('templateEditor.background.fill', 'Fill color')}
              </Form.Label>
              <LomavisSketchColorPicker
                value={
                  backgroundFillIsTransparent
                    ? '#ffffff'
                    : String(pintura.backgroundColor ?? '#ffffff')
                }
                disabled={layer.locked}
                ariaLabel={t(
                  'templateEditor.background.fill',
                  'Fill color'
                )}
                onChange={(value) => setPintura('backgroundColor', value)}
              />
            </Form.Group>
            {!backgroundFillIsTransparent && (
              <Button
                variant="outline-secondary"
                className="rounded-3"
                disabled={layer.locked}
                onClick={() => setPintura('backgroundColor', 'transparent')}
              >
                {t('templateEditor.background.clearFill', 'Clear fill')}
              </Button>
            )}
            <LomavisImageUpload1
              disabled={layer.locked}
              onFileSelect={onImageSelect}
              imageSrc={layer.image?.src}
              onImageRemove={onImageRemove}
              removeLabel={t('templateEditor.image.remove', 'Remove image')}
              acceptedFileTypes={['image/jpeg', 'image/png']}
              title={t('templateEditor.imageUpload.title', 'Select an image to upload')}
              description={t(
                'templateEditor.imageUpload.description',
                'or drag and drop a PNG or JPG image here'
              )}
              invalidFileMessage={t(
                'templateEditor.imageUpload.invalidFile',
                'Choose a PNG or JPG image.'
              )}
            />
          </>
        )}
        {layer.kind === 'image' && (
          <>
            <LomavisImageUpload1
              disabled={layer.locked}
              onFileSelect={onImageSelect}
              imageSrc={layer.image?.src}
              onImageRemove={onImageRemove}
              removeLabel={t('templateEditor.image.remove', 'Remove image')}
              acceptedFileTypes={['image/jpeg', 'image/png']}
              title={t('templateEditor.imageUpload.title', 'Select an image to upload')}
              description={t(
                'templateEditor.imageUpload.description',
                'or drag and drop a PNG or JPG image here'
              )}
              invalidFileMessage={t(
                'templateEditor.imageUpload.invalidFile',
                'Choose a PNG or JPG image.'
              )}
            />
            <Form.Group>
              <Form.Label>
                {t('templateEditor.imageFit', 'Image fit')}
              </Form.Label>
              <BootstrapDropdown
                value={
                  layer.image?.fit === 'natural'
                    ? 'contain'
                    : layer.image?.fit ?? 'cover'
                }
                disabled={layer.locked}
                ariaLabel={t('templateEditor.imageFit', 'Image fit')}
                onChange={(value) => setPintura('backgroundSize', value)}
                options={[
                  {
                    value: 'contain',
                    label: t('templateEditor.fit.contain', 'Contain')
                  },
                  {
                    value: 'cover',
                    label: t('templateEditor.fit.cover', 'Cover')
                  },
                  {
                    value: 'stretch',
                    label: t('templateEditor.fit.stretch', 'Stretch to fill')
                  }
                ]}
              />
            </Form.Group>
            <div
              className="d-grid gap-2"
              style={{ gridTemplateColumns: '1fr 1fr' }}
            >
              <Form.Group>
                <Form.Label>
                  {t('templateEditor.image.cornerRadius', 'Corner radius')}
                </Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  value={Number(pintura.cornerRadius ?? 0)}
                  disabled={layer.locked}
                  onChange={(event) =>
                    setPintura('cornerRadius', Number(event.target.value))
                  }
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>
                  {t('templateEditor.image.opacity', 'Opacity')}
                </Form.Label>
                <InputGroup>
                  <Form.Control
                    type="number"
                    min="0"
                    max="100"
                    value={Math.round(Number(pintura.opacity ?? 1) * 100)}
                    disabled={layer.locked}
                    onChange={(event) =>
                      setPintura('opacity', Number(event.target.value) / 100)
                    }
                  />
                  <InputGroup.Text
                    className={layer.locked ? 'text-muted' : ''}
                  >
                    %
                  </InputGroup.Text>
                </InputGroup>
              </Form.Group>
            </div>
            <div
              className="d-grid gap-2"
              style={{ gridTemplateColumns: '1fr 1fr' }}
            >
              <Form.Group>
                <Form.Label>
                  {t('templateEditor.image.borderColor', 'Border color')}
                </Form.Label>
                <LomavisSketchColorPicker
                  value={String(pintura.borderColor ?? '#000000')}
                  disabled={layer.locked}
                  ariaLabel={t(
                    'templateEditor.image.borderColor',
                    'Border color'
                  )}
                  onChange={(value) => setPintura('borderColor', value)}
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>
                  {t('templateEditor.image.borderWidth', 'Border width')}
                </Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  value={Number(pintura.borderWidth ?? 0)}
                  disabled={layer.locked}
                  onChange={(event) =>
                    setPintura('borderWidth', Number(event.target.value))
                  }
                />
              </Form.Group>
            </div>
          </>
        )}
      </div>
      <LomavisConfirmationModal
        show={showDeleteConfirmation}
        title={t('templateEditor.deleteLayer.title', 'Delete layer?')}
        description={t(
          'templateEditor.deleteLayer.description',
          'This layer will be permanently removed from the overlay.'
        )}
        cancelLabel={t('templateEditor.actions.cancel', 'Cancel')}
        confirmLabel={t('templateEditor.actions.delete', 'Delete')}
        destructive
        onClose={() => setShowDeleteConfirmation(false)}
        onConfirm={() => {
          setShowDeleteConfirmation(false);
          onDelete();
        }}
      />
    </div>
  );
};
